// src/services/testService.ts
// Data layer for tests. Ports the loading + persistence logic from the
// vanilla-JS test-manager.js into pure async functions (no DOM, no globals).
//
// Loading order (matches the original):
//   1. Try Firestore `tests/{bookIdVariant}_unit{n}_test{ver}` doc and
//      fetch its `downloadUrl`. We try BOTH 'book1' and 'book-1' variants
//      because the admin panel and legacy folders disagree on the slug.
//   2. Fall back to local `/tests/{bookId}/unit{n}/test{ver}.json`.
//
// Persistence:
//   • Saves results into the existing progressSync layer so the test result
//     shows up in the unit's progress doc as a section called
//     `test_<version>` (matches the original).

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { progressSync } from './progressSync';
import type {
  PreviousBest,
  TestData,
  TestQuestion,
  TestQuestionRaw,
  TestSection,
} from '../types/test';

// ── Test loading ─────────────────────────────────────────────────────────────

/**
 * Build the dedup-friendly list of bookId variants. Original logic from
 * test-manager.js — tolerates both 'book1' and 'book-1' Firestore doc IDs.
 */
function bookIdVariants(bookId: string): string[] {
  const variants = [
    bookId,
    bookId.replace(/^book(\d+)$/, 'book-$1'),
    bookId.replace(/^book-(\d+)$/, 'book$1'),
  ];
  return variants.filter((id, i, arr) => id && arr.indexOf(id) === i);
}

/**
 * Load a test JSON. Tries Firestore (admin-uploaded) first, then falls back
 * to the local /tests/ folder. Returns null on total failure.
 */
export async function loadTestData(
  bookId: string,
  unitNum: string,
  version: string
): Promise<TestData | null> {
  // 1. Firestore metadata → downloadUrl.
  for (const bid of bookIdVariants(bookId)) {
    const docId = `${bid}_unit${unitNum}_test${version}`;
    try {
      const snap = await getDoc(doc(db, 'tests', docId));
      if (!snap.exists()) continue;
      const meta = snap.data();
      const url = meta.downloadUrl as string | undefined;
      if (!url) continue;
      const resp = await fetch(url);
      if (!resp.ok) continue;
      const data = (await resp.json()) as TestData;
      console.log(`✅ Test loaded from Firebase (${docId}): ${countQuestions(data)} questions`);
      return data;
    } catch (err) {
      console.warn(`[testService] Firestore lookup failed for ${docId}:`, err);
    }
  }

  // 2. Local fallback.
  const localPath = `/tests/${bookId}/unit${unitNum}/test${version}.json`;
  try {
    const resp = await fetch(localPath);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = (await resp.json()) as TestData;
    console.log(`✅ Test loaded from ${localPath}: ${countQuestions(data)} questions`);
    return data;
  } catch (err) {
    console.error('[testService] All test sources failed:', err);
    return null;
  }
}

function countQuestions(data: TestData): number {
  let n = 0;
  for (const s of Object.values(data.sections || {})) n += (s.questions || []).length;
  return n;
}

// ── Question pool builder (Fisher-Yates shuffle preserved) ───────────────────

export function buildQuestionPool(testData: TestData): TestQuestion[] {
  const pool: TestQuestion[] = [];
  const sections = testData.sections || {};

  for (const [sectionId, section] of Object.entries(sections) as Array<[string, TestSection]>) {
    for (const q of section.questions || []) {
      pool.push({ ...(q as TestQuestionRaw), sectionId, sectionTitle: section.title });
    }
  }

  // Fisher-Yates shuffle (in-place).
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool;
}

// ── Progress persistence (mirrors test-manager.js _saveTestProgress) ─────────

export function unitIdFor(bookId: string, unitNum: string): string {
  return `${bookId}_unit${unitNum}`;
}

export function testSectionKey(version: string): string {
  return `test_${version || 'default'}`;
}

export interface SaveTestProgressInput {
  bookId: string;
  unitNum: string;
  version: string;
  score: number;
  maxScore: number;
  percentage: number;
  passed: boolean;
  elapsedSeconds: number;
}

/**
 * Persist a test result through progressSync — same shape as the original
 * test-manager._saveTestProgress() so the data flows into the unit's
 * progress doc and surfaces in BookCard / UnitCard rings.
 */
export async function saveTestProgress(input: SaveTestProgressInput): Promise<void> {
  const { bookId, unitNum, version, score, maxScore, percentage, passed, elapsedSeconds } = input;
  const unitId = unitIdFor(bookId, unitNum);
  const sectionId = testSectionKey(version);

  await progressSync.saveProgress(unitId, sectionId, {
    score,
    maxScore,
    percentage,
    completed: score,
    total: maxScore,
    completedFlag: passed,
    elapsedSeconds,
    lastUpdatedMs: Date.now(),
  });
}

/**
 * Read the previous best score for this test version (if any) so the test
 * shell can show "Best: 87% — PASS" before starting.
 */
export async function loadPreviousBest(
  bookId: string,
  unitNum: string,
  version: string
): Promise<PreviousBest | null> {
  try {
    const unitId = unitIdFor(bookId, unitNum);
    const sectionId = testSectionKey(version);
    const unitProgress = await progressSync.loadProgress(unitId);
    const prev = unitProgress?.[sectionId];
    if (!prev || typeof (prev as { percentage?: number }).percentage !== 'number') {
      return null;
    }
    const p = prev as unknown as Record<string, unknown>;
    return {
      percentage: Number(p.percentage),
      completedFlag: Boolean(p.completedFlag),
      score: typeof p.score === 'number' ? p.score : undefined,
      maxScore: typeof p.maxScore === 'number' ? p.maxScore : undefined,
      elapsedSeconds: typeof p.elapsedSeconds === 'number' ? p.elapsedSeconds : undefined,
    };
  } catch (err) {
    console.warn('[testService] Could not load previous best:', err);
    return null;
  }
}